/**
 * Operations · RAMS dashboard
 *
 * One place to:
 *   • see every RAMS document the system has generated (auto OR manual)
 *   • spot customers whose install is scheduled but their RAMS hasn't been
 *     generated yet (so they can be triggered with a click)
 *   • generate / regenerate a RAMS on demand
 *
 * The actual PDF generation is done by the existing
 * `POST /customers/{id}/rams/regenerate` endpoint — this page is the orchestrator.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  FileText, Loader2, RefreshCw, Search, Sparkles, Download, Eye, CheckCircle2,
  Clock, Calendar, AlertTriangle, FilePlus, Zap, ZapOff, Hammer, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const STATUS_BADGE = {
  scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
  in_progress: 'bg-amber-100 text-amber-700 border-amber-200',
  in_progress_invoiced: 'bg-amber-100 text-amber-700 border-amber-200',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const fmtDate = (iso, { short = false } = {}) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', short
    ? { day: '2-digit', month: 'short' }
    : { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const fmtBytes = (n) => {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export default function RamsOperationsPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('generated');
  const [query, setQuery] = useState('');

  // Generation state
  const [generating, setGenerating] = useState({}); // customer_id → bool
  const [confirmDialog, setConfirmDialog] = useState(null); // {customer, isRegen}

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/operations/rams');
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load RAMS dashboard');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Run the actual generation against the existing endpoint
  const doGenerate = async (customerId, label) => {
    setGenerating((g) => ({ ...g, [customerId]: true }));
    try {
      await api.post(`/customers/${customerId}/rams/regenerate`);
      toast.success(`RAMS generated for ${label}`);
      setConfirmDialog(null);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'RAMS generation failed');
    } finally {
      setGenerating((g) => { const n = { ...g }; delete n[customerId]; return n; });
    }
  };

  const handlePreview = (doc) => {
    if (!doc.document_id) return;
    // Reuse the existing customer-document download endpoint which streams from GridFS
    const url = `/api/customers/${doc.customer_id}/documents/${doc.document_id}/download`;
    window.open(url, '_blank');
  };

  // ─── Filtering (client-side, on top of the API result) ───
  const filteredGenerated = useMemo(() => {
    const list = data?.generated || [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) =>
      r.customer_name?.toLowerCase().includes(q)
      || r.install_address?.toLowerCase().includes(q)
      || r.postcode?.toLowerCase().includes(q),
    );
  }, [data, query]);

  const filteredPending = useMemo(() => {
    const list = data?.pending || [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) =>
      r.customer_name?.toLowerCase().includes(q)
      || r.install_address?.toLowerCase().includes(q)
      || r.postcode?.toLowerCase().includes(q),
    );
  }, [data, query]);

  if (loading) {
    return <Layout><div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></Layout>;
  }
  if (!data) return <Layout><p className="p-8 text-slate-500">Could not load RAMS data.</p></Layout>;

  const { auto_rams_enabled, stats } = data;

  return (
    <Layout>
      <div className="space-y-5 max-w-7xl mx-auto" data-testid="rams-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 font-['Manrope']">
              <FileText className="w-6 h-6 text-blue-600" />
              RAMS Generation
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Risk Assessment & Method Statements for every install — auto-generated when status hits <em>Scheduled</em>, or generate manually below.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={refresh} data-testid="rams-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>

        {/* Auto-RAMS toggle status banner */}
        <Card className={auto_rams_enabled ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/40'}>
          <CardContent className="p-3 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              {auto_rams_enabled ? (
                <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-4 h-4 text-emerald-700" />
                </div>
              ) : (
                <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <ZapOff className="w-4 h-4 text-amber-700" />
                </div>
              )}
              <div>
                <p className="text-sm font-semibold text-slate-900" data-testid="auto-rams-status">
                  Auto-RAMS is {auto_rams_enabled ? 'ON' : 'OFF'}
                </p>
                <p className="text-xs text-slate-600">
                  {auto_rams_enabled
                    ? 'A RAMS is generated automatically when a customer moves to Scheduled.'
                    : 'Auto-generation is disabled — generate RAMS manually using the buttons below.'}
                </p>
              </div>
            </div>
            <Link to="/pipeline-automation" className="text-xs text-blue-700 font-medium hover:underline" data-testid="auto-rams-settings-link">
              Manage auto-actions →
            </Link>
          </CardContent>
        </Card>

        {/* Summary stats */}
        <div className="grid grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Generated</p>
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              </div>
              <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="stat-generated">{stats?.total_generated || 0}</p>
              <p className="text-xs text-slate-500">total RAMS on file</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">This month</p>
                <Sparkles className="w-4 h-4 text-blue-500" />
              </div>
              <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="stat-this-month">{stats?.this_month || 0}</p>
              <p className="text-xs text-slate-500">new RAMS</p>
            </CardContent>
          </Card>
          <Card className={(stats?.pending || 0) > 0 ? 'border-amber-200 bg-amber-50/40' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Pending</p>
                <Clock className={`w-4 h-4 ${(stats?.pending || 0) > 0 ? 'text-amber-600' : 'text-slate-400'}`} />
              </div>
              <p className={`text-2xl font-bold mt-1 ${(stats?.pending || 0) > 0 ? 'text-amber-700' : 'text-slate-900'}`} data-testid="stat-pending">{stats?.pending || 0}</p>
              <p className="text-xs text-slate-500">scheduled installs missing RAMS</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="generated" data-testid="tab-generated">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Generated ({stats?.total_generated || 0})
            </TabsTrigger>
            <TabsTrigger value="pending" data-testid="tab-pending">
              <Clock className="w-3.5 h-3.5 mr-1" /> Pending ({stats?.pending || 0})
            </TabsTrigger>
          </TabsList>

          {/* Search */}
          <div className="relative max-w-sm mt-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by customer name, address or postcode…"
              className="pl-9"
              data-testid="rams-search"
            />
          </div>

          {/* Generated tab */}
          <TabsContent value="generated" className="mt-3">
            <Card>
              <CardContent className="p-0">
                {filteredGenerated.length === 0 ? (
                  <div className="text-center py-10 px-6">
                    <FileText className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-500 italic">No RAMS documents match this filter.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr className="text-left">
                          <th className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500">Customer</th>
                          <th className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500">Install address</th>
                          <th className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 w-32">Status</th>
                          <th className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 w-32">Generated</th>
                          <th className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 w-20">Size</th>
                          <th className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 text-right w-44">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredGenerated.map((r) => (
                          <tr key={r.document_id || r.customer_id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`rams-row-${r.customer_id}`}>
                            <td className="px-3 py-2.5">
                              <Link to={`/customers/${r.customer_id}`} className="font-medium text-blue-700 hover:underline">{r.customer_name}</Link>
                              <p className="text-[11px] text-slate-500 truncate max-w-[180px]">{r.customer_email}</p>
                            </td>
                            <td className="px-3 py-2.5 text-slate-700">{r.install_address || <em className="text-slate-400">—</em>}</td>
                            <td className="px-3 py-2.5">
                              {r.status && (
                                <Badge className={`${STATUS_BADGE[r.status] || 'bg-slate-100 text-slate-700 border-slate-200'} border text-[10px] capitalize`}>
                                  {(r.status || '').replace(/_/g, ' ')}
                                </Badge>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap" title={fmtDateTime(r.uploaded_at)}>
                              {fmtDate(r.uploaded_at, { short: true })}
                              {r.uploaded_by && <p className="text-[10px] text-slate-400 truncate max-w-[110px]" title={r.uploaded_by}>{r.uploaded_by}</p>}
                            </td>
                            <td className="px-3 py-2.5 text-xs text-slate-500 tabular-nums">{fmtBytes(r.size_bytes)}</td>
                            <td className="px-3 py-2.5 text-right">
                              <div className="inline-flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handlePreview(r)}
                                  className="h-7 text-xs"
                                  data-testid={`download-rams-${r.customer_id}`}
                                >
                                  <Download className="w-3 h-3 mr-1" />
                                  Open
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setConfirmDialog({ customer: r, isRegen: true })}
                                  disabled={!!generating[r.customer_id]}
                                  className="h-7 text-xs"
                                  data-testid={`regenerate-rams-${r.customer_id}`}
                                >
                                  {generating[r.customer_id] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                                  Regenerate
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pending tab */}
          <TabsContent value="pending" className="mt-3">
            <Card>
              <CardContent className="p-0">
                {filteredPending.length === 0 ? (
                  <div className="text-center py-10 px-6">
                    <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-300 mb-2" />
                    <p className="text-sm text-slate-500 italic">Every scheduled install has a RAMS on file. Nice.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr className="text-left">
                          <th className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500">Customer</th>
                          <th className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500">Install address</th>
                          <th className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 w-32">Status</th>
                          <th className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 w-36">Install date</th>
                          <th className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 text-right w-36">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPending.map((r) => {
                          const installDate = r.confirmed_install_date || r.provisional_install_date;
                          const isProv = !r.confirmed_install_date && r.provisional_install_date;
                          return (
                            <tr key={r.customer_id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`pending-row-${r.customer_id}`}>
                              <td className="px-3 py-2.5">
                                <Link to={`/customers/${r.customer_id}`} className="font-medium text-blue-700 hover:underline">{r.customer_name}</Link>
                                <p className="text-[11px] text-slate-500 truncate max-w-[180px]">{r.customer_email}</p>
                              </td>
                              <td className="px-3 py-2.5 text-slate-700">{r.install_address || <em className="text-slate-400">—</em>}</td>
                              <td className="px-3 py-2.5">
                                {r.status && (
                                  <Badge className={`${STATUS_BADGE[r.status] || 'bg-slate-100 text-slate-700 border-slate-200'} border text-[10px] capitalize`}>
                                    {(r.status || '').replace(/_/g, ' ')}
                                  </Badge>
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">
                                {installDate ? (
                                  <span className="inline-flex items-center gap-1">
                                    <Calendar className="w-3 h-3 text-slate-400" />
                                    {fmtDate(installDate)}
                                    {isProv && <span className="text-amber-600 text-[10px]">(prov)</span>}
                                  </span>
                                ) : <em className="text-slate-400">not set</em>}
                              </td>
                              <td className="px-3 py-2.5 text-right">
                                <Button
                                  size="sm"
                                  onClick={() => setConfirmDialog({ customer: r, isRegen: false })}
                                  disabled={!!generating[r.customer_id]}
                                  className="h-7 text-xs bg-blue-600 hover:bg-blue-700"
                                  data-testid={`generate-rams-${r.customer_id}`}
                                >
                                  {generating[r.customer_id] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FilePlus className="w-3 h-3 mr-1" />}
                                  Generate
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Confirm generate / regenerate dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={(o) => { if (!o) setConfirmDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="confirm-rams-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirmDialog?.isRegen ? (
                <><RefreshCw className="w-5 h-5 text-blue-600" /> Regenerate RAMS</>
              ) : (
                <><Hammer className="w-5 h-5 text-blue-600" /> Generate RAMS</>
              )}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog?.isRegen
                ? 'A new RAMS PDF will be created from the customer\'s current details, replacing the existing one. The old file is preserved in GridFS for audit.'
                : 'A RAMS PDF will be generated from this customer\'s details and attached to their profile.'}
            </DialogDescription>
          </DialogHeader>
          {confirmDialog && (
            <div className="space-y-1 py-2 bg-slate-50 rounded p-3">
              <p className="text-sm font-semibold text-slate-900">{confirmDialog.customer.customer_name}</p>
              <p className="text-xs text-slate-500">{confirmDialog.customer.install_address || 'No install address on file'}</p>
              {confirmDialog.customer.status && (
                <Badge className={`${STATUS_BADGE[confirmDialog.customer.status] || 'bg-slate-100 text-slate-700 border-slate-200'} border text-[10px] capitalize mt-1`}>
                  {(confirmDialog.customer.status || '').replace(/_/g, ' ')}
                </Badge>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(null)}>Cancel</Button>
            <Button
              onClick={() => doGenerate(confirmDialog.customer.customer_id, confirmDialog.customer.customer_name)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={!!generating[confirmDialog?.customer.customer_id]}
              data-testid="confirm-generate-rams-btn"
            >
              {generating[confirmDialog?.customer.customer_id]
                ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generating…</>
                : <><ChevronRight className="w-4 h-4 mr-1" /> {confirmDialog?.isRegen ? 'Regenerate' : 'Generate'}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
